import { useState } from 'react';
import { useFormik } from 'formik';
import { MailOutlined } from '@ant-design/icons';
import { Space, Typography } from 'antd';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import CardWithIcon from 'components/new-ui/base/card/CardWithIcon';
import FormTextArea from 'components/new-ui/composed/formTextArea/FormTextArea';
import useInvoicesConfig from 'hooks/useInvoicesConfig';
import { emailTemplateValidationSchema } from './validationSchema';
import { useSyncDirty } from 'views/invoices/settings/tabs/GeneralSettingsDirtyContext';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { UpdateInvoiceConfigEmailTempaltePayload } from 'store/invoice-config/types';
import { updateInvoiceConfigEmailTemplate } from 'store/invoice-config/client';
import Button from 'components/new-ui/base/button/Button';
import { getErrorMessage } from 'utils/getErrorMessage';
import Notification from 'components/ui/base/notification/Notification';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import { MAX_INPUT_WIDTH } from '../../utils';

const EmailTemplateSection = () => {
    const { config } = useInvoicesConfig();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const formik = useFormik({
        validationSchema: emailTemplateValidationSchema,
        enableReinitialize: true,
        initialValues: {
            subject: config?.emailTemplate?.subject,
            body: config?.emailTemplate?.body,
            footer: config?.emailTemplate?.footer,
        },
        onSubmit: async (values, helpers) => {
            try {
                showGlobalOverlay({ type: 'LoadingOverlay' });
                setIsSaving(true);

                const { subject, body, footer } = values;

                const payload: UpdateInvoiceConfigEmailTempaltePayload = {
                    subject,
                    body,
                    footer,
                };

                await updateInvoiceConfigEmailTemplate(payload);

                setIsEditing(false);

                Notification({
                    type: 'success',
                    messageTitle: 'Update Complete',
                    description: 'Successfully updated configuration.',
                });
            } catch (error) {
                const errorMessage = getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE;
                Notification({
                    type: 'error',
                    messageTitle: 'Update Failed',
                    description: errorMessage,
                });
            } finally {
                hideGlobalOverlay();
                setIsSaving(false);
            }
        },
    });

    useSyncDirty('emailTemplate', formik.dirty);

    return (
        <CardWithIcon
            title="Email Template"
            gap="large"
            iconProps={{
                icon: <MailOutlined />,
                status: 'info',
            }}
            headerActions={
                <Space size={8}>
                    {isEditing && (
                        <Button
                            disabled={isSaving}
                            type="outlined"
                            onClick={() => {
                                formik.resetForm();
                                setIsEditing(false);
                            }}
                        >
                            Cancel
                        </Button>
                    )}
                    <Button
                        disabled={isEditing && !formik.isValid}
                        loading={isSaving}
                        style={{
                            color: 'var(--brand-6)',
                            borderColor: 'inherit',
                        }}
                        onClick={isEditing ? formik.submitForm : () => setIsEditing(true)}
                    >
                        {isSaving ? 'Saving' : isEditing ? 'Save' : 'Edit'}
                    </Button>
                </Space>
            }
            children={
                <>
                    <Space direction="vertical" size={16}>
                        <Typography.Text>
                            To configure the email template, please make sure you insert placeholders where you want
                            dynamic values to appear following this format:
                            <br />${'{customerName}'}, ${'{invoiceNumber}'}, ${'{dueDate}'}.
                        </Typography.Text>

                        <div>
                            <label htmlFor="subject">
                                <LabelRequiredSymbol style={{ marginRight: 8 }} />
                                Subject
                            </label>
                            <FormInput
                                // eslint-disable-next-line no-template-curly-in-string
                                placeholder="e.g Dear ${customerName},"
                                fieldProps={formik.getFieldProps('subject')}
                                fieldMeta={formik.getFieldMeta('subject')}
                                fieldHelper={formik.getFieldHelpers('subject')}
                                inputStyles={{ marginTop: 6, maxWidth: MAX_INPUT_WIDTH }}
                                disabled={!isEditing}
                            />
                        </div>

                        <div>
                            <label htmlFor="body">
                                <LabelRequiredSymbol style={{ marginRight: 8 }} />
                                Body
                            </label>
                            <FormTextArea
                                // eslint-disable-next-line no-template-curly-in-string
                                placeholder="e.g Your invoice ${invoiceNumber} is due on ${dueDate}."
                                fieldProps={formik.getFieldProps('body')}
                                fieldMeta={formik.getFieldMeta('body')}
                                fieldHelper={formik.getFieldHelpers('body')}
                                style={{ marginTop: 6 }}
                                disabled={!isEditing}
                            />
                        </div>

                        <div>
                            <label htmlFor="footer">
                                <LabelRequiredSymbol style={{ marginRight: 8 }} />
                                Footer
                            </label>
                            <FormTextArea
                                placeholder="Enter footer text here"
                                fieldProps={formik.getFieldProps('footer')}
                                fieldMeta={formik.getFieldMeta('footer')}
                                fieldHelper={formik.getFieldHelpers('footer')}
                                style={{ marginTop: 6 }}
                                disabled={!isEditing}
                            />
                        </div>
                    </Space>
                </>
            }
        />
    );
};

export default EmailTemplateSection;
